import React, { useEffect, useRef, useState } from 'react';

export interface ComboboxProps<T> {
  hasSelection: boolean;
  displayValue: string;
  placeholder: string;
  fetchResults: (query: string) => Promise<T[]>;
  onSelect: (item: T) => void;
  onClear: () => void;
  getItemKey: (item: T) => React.Key;
  renderResult: (item: T, state: { focused: boolean }) => React.ReactNode;
  onQueryChange?: (query: string) => void;
  onOpen?: () => void;
  renderBelowResults?: (close: () => void) => React.ReactNode;
  minQueryLength?: number;
  readOnlyWhenSelected?: boolean;
  disabled?: boolean;
}

const MIN_QUERY_HINT = 'Escriba al menos 2 caracteres para buscar';
const EMPTY_HINT = 'Sin resultados';
const LOADING_HINT = 'Buscando...';

function Combobox<T>({
  hasSelection,
  displayValue,
  placeholder,
  fetchResults,
  onSelect,
  onClear,
  getItemKey,
  renderResult,
  onQueryChange,
  onOpen,
  renderBelowResults,
  minQueryLength = 2,
  readOnlyWhenSelected = true,
  disabled = false,
}: ComboboxProps<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setFocusedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (open) onOpen?.();
  }, [open]);

  const runSearch = (q: string) => {
    if (q.length < minQueryLength) {
      setResults([]);
      return;
    }
    const requestId = ++requestIdRef.current;
    setLoading(true);
    fetchResults(q)
      .then((items) => {
        if (requestIdRef.current === requestId) setResults(items);
      })
      .finally(() => {
        if (requestIdRef.current === requestId) setLoading(false);
      });
  };

  const close = () => {
    setOpen(false);
    setQuery('');
    setFocusedIndex(-1);
  };

  const selectItem = (item: T) => {
    onSelect(item);
    close();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setFocusedIndex(-1);
    onQueryChange?.(value);
    runSearch(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && focusedIndex >= 0) {
      e.preventDefault();
      const item = results[focusedIndex];
      if (item) selectItem(item);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setFocusedIndex(-1);
      inputRef.current?.blur();
    }
  };

  const handleClear = () => {
    onClear();
    setQuery('');
    setFocusedIndex(-1);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div className="input-group input-group-sm">
        <input
          ref={inputRef}
          type="text"
          className="form-control"
          value={displayValue || query}
          onChange={handleChange}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          readOnly={readOnlyWhenSelected && hasSelection}
          disabled={disabled}
        />
        {hasSelection && (
          <button className="btn btn-outline-secondary" type="button" onClick={handleClear}>
            <i className="fa fa-times"></i>
          </button>
        )}
      </div>
      {open && (
        <div
          className="list-group"
          style={{ position: 'absolute', zIndex: 1000, width: '100%', maxHeight: 300, overflowY: 'auto' }}
          role="listbox"
        >
          {loading && (
            <div className="list-group-item text-muted"><i className="fa fa-spinner fa-spin"></i> {LOADING_HINT}</div>
          )}
          {!loading && results.length === 0 && query.length >= minQueryLength && (
            <div className="list-group-item text-muted">{EMPTY_HINT}</div>
          )}
          {query.length < minQueryLength && (
            <div className="list-group-item text-muted small">{MIN_QUERY_HINT}</div>
          )}
          {results.map((item, idx) => (
            <div
              key={getItemKey(item)}
              tabIndex={0}
              role="option"
              aria-selected={focusedIndex === idx}
              className={`list-group-item list-group-item-action${focusedIndex === idx ? ' active' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => selectItem(item)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  selectItem(item);
                }
              }}
            >
              {renderResult(item, { focused: focusedIndex === idx })}
            </div>
          ))}
          {renderBelowResults?.(close)}
        </div>
      )}
    </div>
  );
}

export default Combobox;
